import React, { useState, useEffect } from 'react';
import {
  Box,
  TextInput,
  Button,
  Checkbox,
  Group,
  Image as MantineImage,
} from '@mantine/core';
import axios from '../api/axios';
import { Editor } from '@tinymce/tinymce-react';
import { getTinymceInit } from '../shared/tinymceConfig';
import { useAuth } from '../context/AuthContext';
import styles from './PostForm.module.css';

interface PostFormProps {
  onSuccess?: () => void;
  existingPost?: {
    id: number;
    title: string;
    content: string;
    isPinned: boolean;
    postImageUrl?: string;
    isVideoPost?: boolean;
    videoUrl?: string;
  };
}

const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY  || '';
const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5069';

const PostForm: React.FC<PostFormProps> = ({ onSuccess, existingPost }) => {
  // Import the logged-in user from AuthContext
  const { user } = useAuth();

  // Form field state
  const [title, setTitle] = useState(existingPost?.title || '');
  const [isPinned, setIsPinned] = useState(existingPost?.isPinned || false);
  const [postImageUrl, setPostImageUrl] = useState(existingPost?.postImageUrl || '');
  const [isVideoPost, setIsVideoPost] = useState(existingPost?.isVideoPost || false);
  const [videoUrl, setVideoUrl] = useState(existingPost?.videoUrl || '');
  // Use TinyMCE as a controlled editor for post content
  const [content, setContent] = useState(existingPost?.content || '');
  const [error, setError] = useState<string | null>(null);

  // When existingPost changes, sync state
  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title || '');
      setIsPinned(existingPost.isPinned || false);
      setIsVideoPost(existingPost.isVideoPost || false);
      setVideoUrl(existingPost.videoUrl || '');
      setPostImageUrl(existingPost.postImageUrl || '');
      setContent(existingPost.content || '');
    }
  }, [existingPost]);

  // For inline post image uploads (for the post thumbnail)
  const handlePostImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        setPostImageUrl(base64);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // Handle form submission: build a payload and send via axios
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    let postContent = isVideoPost ? '' : content;
    
    // Append author info if not a video post and if user info is available
    if (!isVideoPost && user) {
      // Check if the post content already includes an element with class "post-author" (to avoid duplicate stamping)
      if (!postContent.includes('class="post-author"')) {
        postContent += `<p class="post-author" style="text-align: right; font-style: italic; margin-top: 20px;">Posted by ${user.username}</p>`;
      }
    }
    
    const payload = {
      title,
      content: postContent,
      isPinned,
      postImageUrl,
      isVideoPost,
      videoUrl,
    };

    try {
      if (existingPost) {
        await axios.put(`/posts/${existingPost.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post('/posts', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Reset fields on new post submission
        setTitle('');
        setIsPinned(false);
        setPostImageUrl('');
        setVideoUrl('');
        setIsVideoPost(false);
        setContent('');
      }
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setError(err.response?.data || 'Failed to submit post.');
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      className={styles.postFormContainer}
    >
      <h2>{existingPost ? 'Edit Post' : 'Create a New Post'}</h2>
      {error && <div className={styles.errorMessage}>{error}</div>}

      <TextInput
        placeholder="Post Title"
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
        required
      />

      <Checkbox
        label="This is a video post"
        checked={isVideoPost}
        onChange={(e) => setIsVideoPost(e.currentTarget.checked)}
      />

      {isVideoPost ? (
        <>
          <TextInput
            placeholder="YouTube Video URL"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.currentTarget.value)}
            required
          />
          {videoUrl && (
            <div className={styles.videoPreview}>
              <iframe
                width="100%"
                height="360"
                src={videoUrl.replace('watch?v=', 'embed/')}
                title="Video Preview"
                frameBorder="0"
                allowFullScreen
              ></iframe>
            </div>
          )}
        </>
      ) : (
        <>
          <Group>
            <Button variant="light" onClick={() => { /* Placeholder for additional shortcuts */ }}>
              Rich Text Editor
            </Button>
            <Button variant="light" onClick={() => {
              // Inline image insertion is handled by TinyMCE's toolbar.
            }}>
              Insert Inline Image
            </Button>
          </Group>
          <Editor
            apiKey={tinymceApiKey}
            value={content}
            onEditorChange={(newContent) => setContent(newContent)}
            init={getTinymceInit(apiBase)}
          />
        </>
      )}

      <Group align="start">
        <Checkbox
          label="Pin this post"
          checked={isPinned}
          onChange={(e) => setIsPinned(e.currentTarget.checked)}
        />
      </Group>

      <div>
        <Button variant="outline" onClick={handlePostImageUpload}>
          Upload Post Image
        </Button>
        {postImageUrl && (
          <div className={styles.imagePreview}>
            <MantineImage src={postImageUrl} alt="Post Preview" width={200} radius="md" />
          </div>
        )}
      </div>

      <Button type="submit" color="blue">
        {existingPost ? 'Update Post' : 'Submit'}
      </Button>
    </Box>
  );
};

export default PostForm;